import { Button, LoadingSpinner, TextLink, Typography, toast } from '@neo4j-ndl/react';
import { consoleApi, getApiError, logout, useAuth } from '@nx/state';
import { AuraLogo, Center } from '@nx/ui';
import { Outlet, useLocation, useNavigate } from 'react-router';

import mail from '../../assets/mail.svg';
import { isAccountLinkingRequired, isEmailNotVerified } from './entities/helpers';

export const EmailVerifiedPage = () => {
  const auth = useAuth();
  const [resendEmail, resendEmailRes] = consoleApi.useEmailVerificationMutation();
  const handleResendEmail = () => {
    resendEmail()
      .unwrap()
      .then(() => {
        toast.success('Email resent!', { isCloseable: true });
      })
      .catch(() => {
        toast.danger('Something went wrong sending the verification email!', { isCloseable: true });
      });
  };

  return (
    <div className="flex h-full w-full flex-row items-center justify-center gap-6">
      <div className="flex h-[180px] w-[180px] items-center justify-center">
        <img src={mail} alt="image of an email" draggable="false" width={140} />
      </div>
      <div className="flex max-w-md flex-col gap-10">
        <AuraLogo className="h-8 self-start" />
        <div className="flex flex-col gap-4">
          <h4>Email verification required</h4>
          <Typography variant="body-large" as="div" className="text-neutral-text-weak">
            We&apos;ve sent an email to <b>{auth.user?.profile.email ?? 'your email'}</b>. Verify your email address to
            get started with Neo4j.
          </Typography>
          <Typography variant="body-large" as="div" className="text-neutral-text-weak">
            Check your spam folder if you have trouble finding it.
          </Typography>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <Button onClick={handleResendEmail} isLoading={resendEmailRes.isLoading} size="large" fill="outlined">
              Resend email
            </Button>
            <Button
              onClick={() => {
                void logout();
              }}
              size="large"
              fill="outlined"
              color="neutral"
            >
              Change account
            </Button>
          </div>
          <Typography variant="body-medium">
            <TextLink href="https://support.neo4j.com/s/" type="external">
              Get help
            </TextLink>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export const ValidUserGuard = () => {
  const { isError, error, isLoading } = consoleApi.useGetUserDetailsQuery();
  const navigate = useNavigate();
  const location = useLocation();

  if (isError) {
    const apiError = getApiError(error);
    if (isEmailNotVerified(apiError.reason)) {
      return <EmailVerifiedPage />;
    }

    if (isAccountLinkingRequired(apiError.reason)) {
      const returnTo = location.pathname + location.search;
      void navigate(`/link-accounts?returnTo=${encodeURIComponent(returnTo)}`, {
        state: { email: apiError.extra?.email, connection: apiError.extra?.connection },
      });
      return (
        <Center>
          <LoadingSpinner size="large" />
        </Center>
      );
    }
  }

  if (isLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  return <Outlet />;
};
